class AmpReviewSlider {
  constructor(root) {
    this.root = root;
    this.carousel = root.querySelector('.amp-review-slider__viewport');
    this.track = root.querySelector('.amp-review-slider__track');
    this.cards = this.track ? [...this.track.children] : [];
    this.prevButton = root.querySelector(
      '.amp-review-slider__nav .prev, .testimonials-navigation .prev, .reviews-navigation .prev'
    );
    this.nextButton = root.querySelector(
      '.amp-review-slider__nav .next, .testimonials-navigation .next, .reviews-navigation .next'
    );
    this.dots = [
      ...root.querySelectorAll('.amp-review-slider__dots .dot, .testimonials-dots .dot, .pagination-dot'),
    ];
    this.drag = null;
    this.scrollFrame = null;

    if (!this.carousel || !this.track || !this.cards.length) return;

    this.bindNavigation();
    this.bindPointerDrag();
    this.carousel.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true });
    window.addEventListener('resize', () => this.scheduleUpdate(), { passive: true });
    this.updateControls();
  }

  getStep() {
    if (this.cards.length < 2) return this.cards[0]?.offsetWidth || 0;
    return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
  }

  getActiveIndex() {
    const viewportCenter = this.carousel.scrollLeft + this.carousel.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  scrollToIndex(index) {
    const card = this.cards[Math.max(0, Math.min(index, this.cards.length - 1))];
    if (!card) return;

    this.carousel.scrollTo({
      left: Math.max(0, card.offsetLeft - this.track.offsetLeft - 20),
      behavior: 'smooth',
    });
  }

  bindNavigation() {
    this.prevButton?.addEventListener('click', () => {
      this.carousel.scrollBy({ left: -this.getStep(), behavior: 'smooth' });
    });

    this.nextButton?.addEventListener('click', () => {
      this.carousel.scrollBy({ left: this.getStep(), behavior: 'smooth' });
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToIndex(index));
    });
  }

  bindPointerDrag() {
    this.carousel.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.target.closest('a, button, input, textarea, select')) return;
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.carousel.scrollLeft,
        moved: false,
      };
      this.carousel.classList.add('is-dragging');
      this.carousel.setPointerCapture(event.pointerId);
    });

    this.carousel.addEventListener('pointermove', (event) => {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      const delta = event.clientX - this.drag.startX;
      if (Math.abs(delta) > 3) this.drag.moved = true;
      this.carousel.scrollLeft = this.drag.startScrollLeft - delta;
    });

    const endDrag = (event) => {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.drag = null;
      this.carousel.classList.remove('is-dragging');
    };

    this.carousel.addEventListener('pointerup', endDrag);
    this.carousel.addEventListener('pointercancel', endDrag);
    this.carousel.addEventListener('lostpointercapture', () => {
      this.drag = null;
      this.carousel.classList.remove('is-dragging');
    });
  }

  scheduleUpdate() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateControls();
    });
  }

  updateControls() {
    const maxScroll = Math.max(0, this.carousel.scrollWidth - this.carousel.clientWidth);
    const atStart = this.carousel.scrollLeft <= 1;
    const atEnd = this.carousel.scrollLeft >= maxScroll - 1;

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;

    const activeIndex = this.getActiveIndex();
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
  }
}

const initializeAmpReviewSliders = (root = document) => {
  root.querySelectorAll('[data-amp-review-slider]').forEach((slider) => {
    if (slider.dataset.swipeInitialized === 'true') return;
    slider.dataset.swipeInitialized = 'true';
    new AmpReviewSlider(slider);
  });
};

document.addEventListener('DOMContentLoaded', () => initializeAmpReviewSliders());
document.addEventListener('shopify:section:load', (event) => initializeAmpReviewSliders(event.target));
window.initializeAmpReviewSliders = initializeAmpReviewSliders;
